
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'components/Shared/Toast';
import { Trans, useTranslation } from 'react-i18next';
import { useProfileState, useProfileAbility } from 'context/profile';
import Whoami from 'actions/whoami-react';
import { contractAction, convertToTemplate } from 'api/actions/contracts';
import { fetcher } from 'api/client'
import { useMutation } from 'react-query';
import bootbox from 'components/Modals/Bootbox';
import { noop, downloadFile } from 'utils'
import { useHistory, useParams } from 'react-router-dom';
import { archiverFolder } from 'app-constants';

function useContractActions(contract, action, options = {
  listOnly: false,
  searchOnly: false,
  returnAsObject: false,
  refetch: noop,
  onOpenModal: noop,
  onUpdateContract: noop,
  onOpenCreateContract: noop
}) {
  const history = useHistory()
  const { profile, roles } = useProfileState();
  const profileAbility = useProfileAbility()
  const { listOnly, searchOnly = false, refetch, onOpenModal, onUpdateContract, onOpenCreateContract } = options;
  const { t } = useTranslation(['translation', 'validations', 'glossary'])
  const { childOf } = useParams();

  const { mutate: doContractAction } = useMutation((data) => fetcher(contractAction(contract._id, data.action, data.payload)), {
    onError: (error) => {
      toast.error(t(`errors.${error.message}`));
    }
  })

  const doConvertToTemplate = useCallback(
    async (contractId) => {
      return fetcher(convertToTemplate({ contractId }))
    },
    [],
  )

  const actionList = useMemo(() => {
    const whoami = new Whoami(contract, profile.email, profile._id, roles, profile.organization, profile.groups);

    const actionListObj = {
      EDIT: {
        faIcon: 'pen',
        label: 'contracts.options.edit',
        shouldShow: () => {
          // if (!whoami.isOwner()) return false;
          if (whoami.currentUserHasToVobo()) return false;
          if (listOnly || searchOnly) return true;
          if (whoami.isFolder()) return false;
          if (whoami.isArchived()) return false;
          return true;
        },
        onClick: () => {
          history.push(`/contracts/editor/${contract._id}`);
        }
      },
      RENAME: {
        faIcon: 'pen-square',
        label: 'contracts.options.rename',
        shouldShow: () => {
          if (whoami.isFolder()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isDraft()) return true;
        },
        onClick: async () => {
          const oldName = contract.name;
          const newName = await bootbox.prompt({
            title: t('contracts.options.rename_name'),
            value: oldName
          });
          if (newName === oldName) return;
          if (!newName) return; // { success: false, error: 'NameNotProvided' };

          const payload = {
            name: newName
          }

          doContractAction({ action: 'rename', payload }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                onUpdateContract(payload)
              }
            }
          });

        }

      },
      SET_IN_PROGRESS: {
        faIcon: 'undo',
        label: 'contracts.options.set_in_progress',
        shouldShow: () => {
          if (whoami.isArchived()) return false;
          if (!whoami.isOwner()) return false;
          if (!whoami.contractIsApplication()) return false;
          if (whoami.isFinalized()) return true;
          return false;
        },
        onClick: async () => {
          doContractAction({
            action: 'changeapplicationstatus',
            payload: {
              status: 'IN_PROGRESS'
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });

        },
      },
      FINALIZE: {
        faIcon: 'lock',
        label: 'contracts.options.finalize_contract',
        shouldShow: () => {
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isArchived()) return false;
          if (whoami.contractIsApplication() && !whoami.isFinalized()) return true;
          if (whoami.canBeFinalized()) return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.finalize_application'));
          if (!confirm) return;

          doContractAction({ action: 'finalize' },
            {
              onSuccess: (actionResult) => {
                if (actionResult?.success) {
                  toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                  refetch({ refreshDashboard: true })
                  // onUpdateContract(payload)
                }
              }
            });
        }

      },
      // SHARE: {
      //   name: 'SHARE',
      //   faIcon: 'users',
      //   label: 'Compartir',
      //   action: 'sharecontract',
      //   shouldShow: () => false,
      //   onClick(contract) {
      //     // if(view)
      //     //   view.destroy();
      //     AppUtil.showShareContractModal(model);
      //   },
      // }
      // RETURN_LAWYER: {
      //     name: 'RETURN_LAWYER',
      //     faIcon: 'share',
      //     label: '<b>' + t('contracts.options.return_lawyer') + '</b>',
      //     action: 'send-lawyer',
      //     shouldShow: () => false,
      //     onClick(contract) {
      //       bootbox.confirm(t('contracts.options.confirm_return_lawyer'), (resp) => {
      //         if (resp) {
      //           const profile = app.request('profile');
      //           const delegationModel = new DelegationModel();
      //           delegationModel.set('stage', 'correction-review');
      //           delegationModel.url = `/collaboration/delegate/${model.get('_id')}/${profile.get('accountRoot')}`;
      //           delegationModel.save().then(() => {
      //             app.trigger('reload-collections');
      //           });
      //         }
      //       });
      //     }
      //   },
      // OPEN_WORD_EDIT: {
      //   name: 'OPEN_WORD_EDIT',
      //   icon: '/images/ui/word-online-blue.png',
      //   label: 'contracts.options.edit_word',
      //   action: 'edit-word',
      //   shouldShow: () => {
      //     if (whoami.isArchived()) return false;
      //     if (!whoami.isOwner()) return false;
      //     if (whoami.isPdf()) return false;
      //     if (whoami.isFinalized()) return false;
      //     if (whoami.isSigned()) return false;
      //     return whoami.isDocOrDocx();
      //   },
      //   onClick(contract) {
      //     // if(view)
      //     //   view.destroy();
      //     if (model.get('workdocs')) {
      //       window.open('https://tratoapp.awsapps.com/workdocs/index.html#/document/' + model.get('workdocs').id);
      //     } else {
      //       window.open(`${window.location.origin}/wordonline/${model.get('_id')}`, '_blank');
      //     }
      //   },
      // } 
      OPEN_EXCEL_EDIT: {
        name: 'OPEN_EXCEL_EDIT',
        icon: '/images/ui/excel-online-blue.png',
        label: 'contracts.options.edit_excel',
        action: 'edit-excel',
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          if (whoami.isPdf()) return false;
          if (whoami.isFinalized()) return false;
          if (whoami.isSigned()) return false;
          return whoami.isXlsOrXlsX();
        },
        onClick: () => {
          if (contract.workdocs) {
            window.open(`https://tratoapp.awsapps.com/workdocs/index.html#/document/${contract.workdocs.id}`);
          } else {
            window.open(`${window.location.origin}/wordonline/${contract._id}`, '_blank');
          }
        },
      },
      CREATE_REFERENCED_CONTRACT: {
        faIcon: 'retweet',
        label: 'contracts.options.referenced_document',
        shouldShow: () => {
          if (listOnly) return false;
          if (whoami.contractIsApplication() && whoami.isFinalized()) return false;
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (whoami.isChildContract()) return false;
          return true;
        },
        onClick: () => {
          onOpenCreateContract({ referenceId: contract._id });
        }

      },
      RENEW: {
        faIcon: 'redo',
        label: 'Renovar',
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          if ((whoami.isSigned() || whoami.isFinalized()) && whoami.isAboutToExpire(profile?.tenant?.expirationDaysFilter)) return true;
          if (whoami.isExpired()) return true;
          return false;
        },
        onClick: () => {
          return onOpenModal({ name: 'RENEW' });
        }

      },
      ARCHIVE: {
        mustRefetch: true,
        faIcon: 'archive',
        label: 'contracts.options.archive',
        shouldShow: () => {
          if (whoami.isChildContract()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isFolder()) return false;
          return childOf !== archiverFolder;
        },
        onClick: async () => {
          doContractAction({
            action: 'archive',
            payload: {
              archive: true
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      SET_REFERENCE: {
        mustRefetch: true,
        faIcon: 'link',
        label: 'contracts.options.reference',
        shouldShow: () => {
          if (listOnly) return false;
          if (whoami.contractIsApplication() && whoami.isFinalized()) return false;
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (whoami.isChildContract()) return false;
          if (whoami.isParentContract()) return true;
          if (whoami.isDraft()) return true;
          if (whoami.isSigned()) return true;
          if (whoami.contractIsApplication()) return true;
          return true;
        },
        onClick: () => {
          onOpenModal({ name: 'SET_REFERENCE' })
        },

      },
      INIT_WORKFLOW: {
        mustRefetch: true,
        faIcon: 'play',
        label: 'workflow.actions.init',
        shouldShow: () => {
          if (whoami.isFinalized()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isSigned()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isPreApproved()) return false;
          if (whoami.isSigningOrCompleting() || whoami.isInNegotiation()) return false;
          if (whoami.hasWorkflow() && !whoami.workflowIsInitiated()) return true;
        },
        onClick: async () => {
          doContractAction({
            action: 'workflowactions',
            payload: {
              workflowAction: 'init'
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      CONTINUE_WORKFLOW: {
        mustRefetch: true,
        faIcon: 'play',
        label: 'workflow.actions.resume',
        shouldShow: () => false,
        onClick: async () => {
          doContractAction({
            action: 'workflowactions',
            payload: {
              workflowAction: 'resume'
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      DELETE_WORKFLOW: {
        faIcon: 'trash',
        label: 'workflow.actions.delete',
        shouldShow: () => {
          if (whoami.isOwner() && whoami.hasWorkflowStopped()) return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.confirm_delete_workflow'));
          if (!confirm) return;
          doContractAction({
            action: 'workflowactions',
            payload: {
              workflowAction: 'remove'
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      STOP_WORKFLOW: {
        faIcon: 'stop',
        label: 'workflow.actions.stop',
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.hasWorkflowInitiated()) return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.confirm_stop_workflow'));
          if (!confirm) return;
          doContractAction({
            action: 'workflowactions',
            payload: {
              workflowAction: 'stop'
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      UNLINK_REFERENCE: {
        mustRefetch: true,
        faIcon: 'unlink',
        label: 'contracts.options.unlink_reference',
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (whoami.isChildContract()) return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.documents.reference_delete_confirm'));
          if (!confirm) return;

          doContractAction({ action: 'unlink' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });


        }

      },
      // DOWNLOAD_REPORT: {
      //   name: 'DOWNLOAD_REPORT',
      //   faIcon: 'bar-chart',
      //   label: 'contracts.options.report',
      //   action: 'getreport',
      //   shouldShow: () => false,
      //   onClick(contract) {
      //     // if(view)
      //     //   view.destroy();
      //     bootbox.confirm(t('contracts.options.report_confirm'), (result) => {
      //       if (result) {
      //         const report = new Model({ contract: model.get('_id') });
      //         report.url = '/contracts/reports/time';
      //         report.save().then((res) => {
      //           window.open(res.url);
      //         });
      //       }
      //     });
      //   },
      // },
      // SET_OWNER: {
      //   name: 'SET_OWNER',
      //   faIcon: 'key',
      //   label: 'contracts.options.ownership',
      //   action: 'setmine',
      //   shouldShow: () => false,
      //   onClick(contract) {
      //     if (view)
      //       view.destroy();
      //     bootbox.confirm(t('contracts.options.ownership_confirm'), (result) => {
      //       if (result) {
      //         const profile = app.request('profile');
      //         const userId = profile.get('_id');
      //         model.save({ user: userId }, { patch: true })
      //           .then(() => {
      //             const collection = app.request('collections:contracts');
      //             collection.fetch({ reset: true })
      //               .then(() => {
      //                 collection.original = collection.original.models;
      //                 app.request('modals').destroyAll();
      //                 app.request('modals').show(new Success({ message: t('contracts.options.ownership_success') }));
      //               });
      //           });
      //       }
      //     });
      //   },
      // }, 
      SET_SIGNED: {
        faIcon: 'check',
        label: 'contracts.options.set_signed',
        shouldShow: () => {
          if (listOnly) return false;
          if (whoami.isFolder()) return false;
          if (!whoami.isOwner()) return false;
          return profileAbility.can('set_signed', contract);
          // if (!whoami.isPdf()) return false;
          // if (whoami.isDraft() && whoami.hasNoSignature()) return true;
          // if (whoami.isSentToDownload() && whoami.hasNoSignature()) return true;
          // if (whoami.isDraft() && whoami.hasNoSignature()) return true;
          // if (whoami.isSentToDownload() && whoami.hasNoSignature()) return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm({ textT: 'contracts.options.set_signed_confirm' });
          if (!confirm) return;

          doContractAction({ action: 'setsigned' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });

        }
      },
      // RESEND_SMS: {
      //   faIcon: 'mobile',
      //   label: 'contracts.options.resend_sms',
      //   action: 'resendsms',
      //   shouldShow: () => {
      //     if (!whoami.isOwner()) return false;
      //     if (whoami.isArchived()) return false;
      //     if (whoami.isDraft()) return false;
      //     if (whoami.isSigned()) return false;
      //     if (whoami.isPasswordProtected()) return true;
      //   },
      //   onClick: async () => {
      //     const confirm = await bootbox.confirm(t('contracts.options.resend_sms_confirm'));
      //     if (!confirm) return;

      //     const actionResult = await doContractAction({action: 'resendsms');

      //     if (actionResult?.success) {
      //       toast.success(t('success'));
      //       refetch({ refreshDashboard: true })
      //       // onUpdateContract(payload)
      //     } 
      //   },
      // },
      UNARCHIVE: {
        mustRefetch: true,
        faIcon: 'box-open',
        label: 'contracts.options.unarchive',
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          return childOf === archiverFolder
        },
        onClick: async () => {
          doContractAction({
            action: 'archive',
            payload: {
              archive: false
            }
          }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      // CL_SHOW: {
      //   faIcon: 'lock',
      //   label: 'text',
      //   shouldShow: () => false
      // },
      // CL_ARCHIVE: {
      //   faIcon: 'lock',
      //   label: 'text',
      //   shouldShow: () => false
      // },
      // CL_DELETE: {
      //   faIcon: 'lock',
      //   label: 'text',
      //   shouldShow: () => false
      // },
      // CL_DOWNLOAD: {
      //   faIcon: 'lock',
      //   label: 'text',
      //   shouldShow: () => false
      // },
      DELETE: {
        mustRefetch: true,
        faIcon: 'trash',
        label: 'contracts.options.delete',
        shouldShow: () => {
          if (whoami.hasWorkflowInitiated()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isChildContract()) return false;
          if (whoami.isCanceled() || whoami.isArchived()) return true;
        },

        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.delete_confirm'));
          if (!confirm) return;

          doContractAction({ action: 'remove' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyDeleted', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      REVIEW: {
        faIcon: 'pen',
        label: 'contracts.options.review',
        shouldShow: () => false,
        onClick() {
          history.push(`/contracts/editor/${contract._id}`);
        }

      },
      VIEW_DETAIL: {
        faIcon: 'pen',
        label: 'contracts.options.detail',
        shouldShow: () => false,
        onClick() {
          history.push(`/contracts/editor/${contract._id}`);
        }

      },
      CONVERT: {
        faIcon: 'redo',
        label: 'contracts.options.convert',
        shouldShow: () => {
          if (whoami.contractIsApplication()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (whoami.isChildContract()) return false;
          if (whoami.isArchived()) return false;
          return profileAbility.can('convert', contract);
        },
        onClick: async () => {
          const actionResult = await doConvertToTemplate(contract._id);

          if (actionResult?.success) {
            toast.success(t('validations:messages.successfullyCreated', { context: 'male', entity: t('glossary:template') }));
            refetch({ refreshDashboard: true })
            // onUpdateContract(payload)
          }
        }
      },
      DUPLICATE: {
        faIcon: 'copy',
        label: 'contracts.options.duplicate',
        shouldShow: () => {
          if (whoami.contractIsApplication()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (whoami.isChildContract()) return false;
          if (whoami.isArchived()) return false;
          return true;
        },
        onClick: () => {
          onOpenModal({ name: 'DUPLICATE' })
        },
      },
      DOWNLOAD_PDF: {
        faIcon: 'download',
        label:  `${t('contracts.options.download')} (PDF)`,
        shouldShow: () => {
          // return false;
          if (listOnly || searchOnly) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isPdf()) return true;
          return false
        },
        onClick: async () => {
          doContractAction({ action: 'downloadpdf' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.url) {
                downloadFile({ url: actionResult.url, name: contract.name, ext: 'pdf' })()
                // toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract')}));
                // refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });

        }
      },
      DOWNLOAD_WORD: {
        faIcon: 'download',
        label: `${t('contracts.options.download')} (WORD)`,
        shouldShow: () => {
          // return false;
          if (listOnly || searchOnly) return false;
          if (whoami.isCanceled()) return false;
          if (whoami.isWordOnline()) return true;
          return false
        },
        onClick: async () => {
          doContractAction({ action: 'downloadword' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.url) {
                downloadFile({ url: actionResult.url, name: contract.name, ext: 'docx' })()
                // refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      DOWNLOAD_HTML: {
        faIcon: 'download',
        label: 'contracts.options.download',
        shouldShow: () => {
          // return false;
          if (listOnly || searchOnly) return false;
          if (whoami.isCanceled()) return false;
          if (!whoami.isPdf() && !whoami.isWordOnline()) return true;
          return false
        },
        onClick: async () => {
          doContractAction({ action: 'downloadhtml' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.url) {
                downloadFile({ url: actionResult.url, name: contract.name, ext: 'pdf' })()
                // refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      FILL: {
        faIcon: 'file',
        label: 'contracts.options.vobo',
        shouldShow: () => false,
        onClick: () => {
          window.open(`/sign/${contract._id}`);
        }
      },
      SHOW: {
        faIcon: 'file',
        label: 'contracts.options.view',
        shouldShow: () => {
          // if (listOnly || searchOnly) return true;
          if (whoami.isSentToDownload()) return true;
          if (whoami.isSentToComplete()) return true;
          if (whoami.isBeingNegotiated()) return true;
          if (whoami.isSentToSign()) return true;
          if (whoami.isSigned()) return true;
        },
        onClick: () => {
          window.open(`/sign/${contract._id}`);
        }
      },
      SIGN: {
        faIcon: 'file-signature',
        label: 'contracts.options.sign',
        shouldShow: () => whoami.hasToSign() || whoami.hasToComplete(),
        onClick: () => {
          window.open(`/sign/${contract._id}`);
        }
      },
      CANCEL: {
        faIcon: 'ban',
        label: 'contracts.options.cancel',
        shouldShow: () => {
          if (whoami.isSigned()) return false;
          if (whoami.isFinalized()) return false;
          if (whoami.isArchived()) return false;
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isFolder()) return false;
          return true;
        },
        onClick: async () => {
          const cancelReason = await bootbox.prompt(t('contracts.options.cancel_confirm_prompt'));
          if (cancelReason === null) return;
          doContractAction({ action: 'cancel', payload: { cancelReason } }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      MOVE_TO: {
        faIcon: 'folder-open',
        label: 'contracts.options.move',
        shouldShow: () => {
          if (whoami.isInsideRecord()) return false;
          if (whoami.isFolder()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isChildContract()) return false;
          return true;
        },
        onClick: () => {
          onOpenModal({ name: 'MOVE_TO' })
        },
      },
      RESET: {
        faIcon: 'undo',
        label: 'contracts.options.restart_status',
        shouldShow: () => {
          if (whoami.isFinalized()) return false;
          if (whoami.isSigned()) return false;
          if (whoami.hasWorkflowInitiated()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isDraft()) return false;
          if (whoami.isExpired()) return false;
          return true;
        },
        onClick: async () => {
          const confirm = await bootbox.confirm({ textT: 'contracts.options.restart_status_warning' });
          if (!confirm) return;

          doContractAction({ action: 'reset' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }
      },
      // RECORDTAKEOUT: {
      //   name: 'RECORDTAKEOUT',
      //   faIcon: 'upload',
      //   label: 'records.options.take_out',
      //   action: 'recordtakeout',
      //   shouldShow: () => {
      //     return false; //if (!whoami.isOwner()) return false;
      //     //if (whoami.isInsideRecord()) return true;
      //   },
      //   onClick(contract) {
      //     // if(view)
      //     //   view.destroy();
      //     const reset = new Model();
      //     reset.url = `/records/takeout/${model.get('_id')}`;
      //     reset.save().then(() => {
      //       app.trigger('reload-collections');
      //     });
      //   },
      // },
      REMOVE_LOCK: {
        faIcon: 'unlock',
        label: 'contracts.options.unlock',
        shouldShow: () => {
          if (whoami.isCanceled()) return false;
          if (!whoami.isOwner()) return false;
          if (whoami.isTemplate()) return false;
          if (whoami.isLocked() && whoami.roleCan('REMOVE_LOCK')) return true;
          return false;
        },
        onClick: async () => {
          doContractAction({ action: 'unlock' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });
        }

      },
      GRAB_VOBO: {
        faIcon: 'hand-paper',
        label: 'contracts.options.grab_vobo',
        shouldShow: () => whoami.myGroupCanTakeVobo(),
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.grab_confirm'));
          if (!confirm) return;

          doContractAction({ action: 'grabvobo' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          });

        }

      },
      GRAB: {
        faIcon: 'hand-holding',
        label: 'contracts.options.grab',
        shouldShow: () => whoami.myGroupCanGrab(),
        onClick: async () => {
          const confirm = await bootbox.confirm(t('contracts.options.grab_confirm'));
          if (!confirm) return;

          doContractAction({ action: 'grab' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
                // onUpdateContract(payload)
              }
            }
          }
          );
        }
      },
      VOBO: {
        faIcon: 'thumbs-up',
        label: 'contracts.options.vobo',
        shouldShow: () => whoami.currentUserHasToVobo(),
        onClick: () => {
          history.push(`/contracts/editor/${contract._id}`);
        }
      },
      ANALIZE_METADATA: {
        faIcon: 'diagnoses',
        label: 'contracts.options.metadata',
        shouldShow: () => whoami.isPdf() && !whoami.hasMetadata(),
        onClick: async () => {
          doContractAction({ action: 'extractText' }, {
            onSuccess: (actionResult) => {
              if (actionResult?.success) {
                toast.success(t('validations:messages.successfullyAnalysed', { context: 'male', entity: t('glossary:contract') }));
                refetch({ refreshDashboard: true })
              }
            }
          });

        }
      },
    };
    // const filteredActionEntries = Object.entries(actionListObj).filter(([key, action]) => action.shouldShow())
    // console.log({filteredActionEntries})
    const filteredActions = Object.fromEntries(Object.entries(actionListObj).filter?.(([key, action]) => action?.shouldShow?.()).map?.(([key, action]) => ([key, { ...action, name: key }]))) ?? {}

    if (options.returnAsObject) {
      return filteredActions
    }


    if (action) {
      if (action === 'DOWNLOAD') {
        const downloadHTML = filteredActions['DOWNLOAD_HTML']
        const downloadWORD = filteredActions['DOWNLOAD_WORD']
        const downloadPDF = filteredActions['DOWNLOAD_PDF']

        return downloadWORD ?? downloadPDF ?? downloadHTML;
      }

      return filteredActions[action]
    }

    return Object.values(filteredActions)
  }, [contract, profile.email, profile._id, profile.organization, profile.groups, roles, options.returnAsObject, action, listOnly, searchOnly, doContractAction, t, onUpdateContract, refetch, onOpenCreateContract, onOpenModal, profileAbility, doConvertToTemplate]);

  // useEffect(() => {
  //   setAvailableActions(actionList);
  // }, [actionList]);

  return actionList;
}

export default useContractActions;